import React, { useContext, useMemo, useState } from 'react'
import { QueryResult } from '@apollo/react-common'
import { Order, OrderCtx } from './model-order'
import * as R from 'ramda'

import { useQuery } from '@apollo/react-hooks'
import { RestaurantsAPI5ClientCtxx } from 'context'
import { GET_RESTAURANT, RestaurantCtx } from 'views/order/v5/model-restaurant'
import { extractNotifications } from 'views/order/v5/notifications/model'
import { UserConfigCtx } from 'views/order/v5/comp-factory'
import IntegrationInfo from './integration'
import PaymentsInfo from './payments'

import { printOrderExternalWindow as printOrder } from './print'

import OrderInfo from './fragments/info'
import * as Tab from './fragments/tab-view'
import OrderDetails from 'views/order/v5/fragments/details'
import {
  useOrderStatusConnect,
  NodeChangedEvent,
  useOrdersPresence,
  OrderStatus,
  getOrderActions,
  getOrderEvents,
  getOrderLogs,
  pushToOrderChannel,
} from './api/order-state'

import { Feedback2Svc } from './api/feedback2'
import Support from './support'
import OrderTimeline from './timeline'

import { Button } from 'reactstrap'

import userSettingsAPI from 'stores/user'
import client from 'api/auth'

import { ChannelCtx, GET_CHANNEL } from 'views/order/v5/model-channel'
import { AuditResource } from 'api/audit'
import { WNode, WNodeAction, Workflow, WorkflowInfo, WManualTransition, WManualTransitionCategory } from './ow/model'
import { ZupplerLogsContext } from 'views/zuppler_logs'

declare let ORDERS_SVC_API5: string

export interface OrderV5Props {
  order: Order
  reloadOrder: () => void
  paymentsQuery: QueryResult<any>
}
export default function OrderV5({ order, reloadOrder, paymentsQuery }: OrderV5Props): JSX.Element {
  const restaurantClient = useContext(RestaurantsAPI5ClientCtxx)

  if (!restaurantClient) {
    return <>Invalid configuration...</>
  }

  const restaurantQuery = useQuery(GET_RESTAURANT, {
    variables: {
      restaurant_id: order.carts.restaurant_id,
      workflow_id: order.workflow_id,
    },
    client: restaurantClient,
  })

  const channelQuery = useQuery(GET_CHANNEL, {
    variables: {
      channel_id: order.carts.channel_id, //order.carts.channel_id,
      integration_id: order.carts.integration_id,
      settingsType: 'web5',
    },
    client: restaurantClient,
  })

  const orderChannel = useOrderStatusConnect('order-info', order)
  // const { nodeActions, push, workflowEvents, workflowLogs } = orderStatus

  const presence = useOrdersPresence()
  const orderPresence = R.defaultTo([], R.path([order.uuid, 'metas'], presence))

  if (restaurantQuery.loading || channelQuery.loading) {
    return <></>
  }
  if (restaurantQuery.error) {
    return (
      <div>
        <pre>{JSON.stringify(restaurantQuery.error, null, 2)}</pre>
      </div>
    )
  }

  if (channelQuery.error) {
    return (
      <div>
        <pre>{JSON.stringify(channelQuery.error, null, 2)}</pre>
      </div>
    )
  }

  const onExecuteWorkflowAction = (id: string): void => {
    console.log('Execute manual transition', id)
    pushToOrderChannel(orderChannel, 'trigger_transition', { id })
  }

  const onExecuteRestaurantEvent = (id: string): Promise<{ orders: number }> => {
    const url = ORDERS_SVC_API5 + '/v5/webhooks/restaurant_event_trigger'
    const token = client.getAuthResponse().access_token
    const payload = {
      restaurant_id: order.carts.restaurant_id,
      event_type: id,
    }
    return fetch(url, { method: 'POST', headers: authHeaders(token), body: JSON.stringify(payload) }).then(
      processJsonStatus
    )
  }

  const onPrintOrder = () => printOrder(order.uuid)

  const userConfigProxy = {
    getConfig: (s: string) => userSettingsAPI.settingFor(s),
    setConfig: (s: string, v: string | boolean | number) => userSettingsAPI.setSettingFor(s, v),
  }

  const tabs = [
    { id: 'details', label: 'Order Details' },
    { id: 'payment', label: 'Payment' },
    { id: 'support', label: 'Support' },
    { id: 'other', label: 'Order Source' },
  ]

  const nodeActions = getOrderActions(orderChannel)
  const events = getOrderEvents(orderChannel, order)
  const logs = getOrderLogs(orderChannel, order)
  const extraEvents = order.order_workflow.events || []

  return (
    <ChannelCtx.Provider value={channelQuery.data?.channel}>
      <UserConfigCtx.Provider value={userConfigProxy}>
        <RestaurantCtx.Provider value={restaurantQuery.data.restaurant}>
          <OrderCtx.Provider value={order}>
            <Feedback2Svc orderId={order.uuid}>
              <OrderStatus channel={orderChannel}>
                <div className="row">
                  <div className="col-12">
                    <OrderInfo presence={orderPresence || []} />
                    <OrderTimeline
                      key={order.uuid}
                      workflow={restaurantQuery.data.restaurant.workflow.content}
                      logs={logs.reverse()}
                      actions={nodeActions}
                      onExecuteAction={onExecuteWorkflowAction}>
                      <RestaurantEventActions
                        currentWorkflowNode={nodeActions ? nodeActions.node : null}
                        workflow={restaurantQuery.data.restaurant.workflow.content}
                        onExecuteAction={onExecuteRestaurantEvent}
                      />
                      <OrderActions
                        actions={nodeActions}
                        onExecuteAction={onExecuteWorkflowAction}
                        workflow={restaurantQuery.data.restaurant.workflow.content}
                      />
                      <PrintAction onPrint={onPrintOrder} />
                    </OrderTimeline>
                    <Tab.Selector tabs={tabs}>
                      <Tab.Content key="details" for="details">
                        <OrderDetails reloadOrder={reloadOrder} />
                      </Tab.Content>
                      {restaurantQuery.data && nodeActions && restaurantQuery.data.restaurant.workflow.content && (
                        <Tab.Content key="support" for="support">
                          <Support
                            workflowInfo={restaurantQuery.data.restaurant.workflow}
                            workflow={restaurantQuery.data.restaurant.workflow.content}
                            order={order}
                            currentWorkflowNode={nodeActions}
                            notifications={extractNotifications(restaurantQuery.data.restaurant.notifications)}
                            events={events}
                            logs={logs}
                            extraEvents={extraEvents}
                          />
                        </Tab.Content>
                      )}
                      {restaurantQuery.data && channelQuery.data && (
                        <Tab.Content key="other" for="other">
                          <IntegrationInfo
                            restaurant={restaurantQuery.data.restaurant}
                            channel={channelQuery.data.channel}
                            order={order}
                          />
                        </Tab.Content>
                      )}
                      {!paymentsQuery.loading && paymentsQuery.data && (
                        <Tab.Content key="payments" for="payment">
                          {paymentsQuery.data.order != null ? (
                            <AuditResource resourceType="order" resourceId={order.uuid}>
                              <ZupplerLogsContext
                                ownerType="restaurant"
                                ownerId={String(order.carts.restaurant_id)}
                                sessionType="payment_flow"
                                sessionId={order.uuid}>
                                <PaymentsInfo
                                  orderId={order.uuid}
                                  data={order}
                                  order={paymentsQuery.data.order}
                                  skipAdjustments={false}
                                  reload={() => {
                                    paymentsQuery.refetch({ order_id: order.uuid })
                                  }}
                                />
                              </ZupplerLogsContext>
                            </AuditResource>
                          ) : (
                            <div>PA1 Order</div>
                          )}
                        </Tab.Content>
                      )}
                    </Tab.Selector>
                  </div>
                </div>
              </OrderStatus>
            </Feedback2Svc>
          </OrderCtx.Provider>
        </RestaurantCtx.Provider>
      </UserConfigCtx.Provider>
    </ChannelCtx.Provider>
  )
}

function classFromCategory(c: WManualTransitionCategory): string {
  switch (c) {
    case 'secondary':
      return 'primary'
    default:
      return c
  }
}

function OrderActions(props: {
  actions: NodeChangedEvent | null
  workflow: Workflow
  onExecuteAction: (id: string) => void
}): JSX.Element {
  const actionWithTransitions = useMemo(() => {
    const wTransitions = props.workflow.transitions.filter(
      (mt: WManualTransition) => mt.transition_type == 'manual' && mt.from_node == props.actions?.node
    ) as WManualTransition[]

    return props.actions?.transitions.filter(R.compose(R.not, R.isNil)).map((t) => {
      const wt = wTransitions.find((wt) => wt.name == t)
      return {
        id: t,
        name: wt?.name,
        category: wt?.category,
        button: classFromCategory(wt?.category || 'primary'),
      }
    })
  }, [props.actions])

  // console.log('{PGDBG} actionsWithTransitions', actionWithTransitions)

  const onExecuteOrderAction = ({ id: t, name, category: trCategory }): void => {
    if (t == 'cancel_order') {
      if (confirm('Are you sure you want to cancel this order?')) {
        props.onExecuteAction(t)
      }
    } else {
      switch (trCategory) {
        case 'danger':
        case 'warn':
          if (confirm(`Are you sure you want to ${name}?`)) {
            props.onExecuteAction(t)
          }
        default:
          props.onExecuteAction(t)
      }
    }
  }
  // console.log('[PGDBG]', props.actions)
  // console.log('[PGDBG]', props.workflow)
  return (
    <>
      {actionWithTransitions?.map((t) => (
        <Button color={t.button} key={t.id} onClick={() => onExecuteOrderAction(t)}>
          {makeWord(t?.name || '')}
        </Button>
      ))}
    </>
  )
}

function PrintAction(props: { onPrint: () => Promise<void> }): JSX.Element {
  const [printing, setPrinting] = useState(false)
  const startPrinting = (): void => {
    setPrinting(true)
    props.onPrint().finally(() => setPrinting(false))
  }
  return (
    <Button color="info" onClick={startPrinting} loading={printing}>
      {printing ? 'Printing...' : 'Print Receipt'}
    </Button>
  )
}
const makeWord = (str: string): string =>
  str
    .split('_')
    .map((str, index) => (index == 0 ? str.slice(0, 1).toLocaleUpperCase() + str.slice(1, str.length) : str))
    .join(' ')

function RestaurantEventActions(props: {
  currentWorkflowNode: null | string
  workflow: Workflow
  onExecuteAction: (id: string) => Promise<{ orders: number }>
}): JSX.Element {
  const [loadingButton, setLoadingButton] = useState<string | null>(null)
  const actions = getSubscribeRestaurantNodeActions(props.currentWorkflowNode, props.workflow)

  const onExecuteEvent = (eventId: string): void => {
    if (
      confirm(
        'This will send the event to all orders waiting for restaurant confirmation. Are you sure you want to continue?'
      )
    ) {
      setLoadingButton(eventId)
      props
        .onExecuteAction(eventId)
        .then(({ orders }) => {
          setLoadingButton(null)
          alert(`The system updated the order state for ${orders} orders`)
        })
        .catch((err: ServerError) => {
          setLoadingButton(null)
          alert(`Failed to update order status: [${err.status}] ${err.statusText}`)
        })
    }
  }
  return (
    <>
      {actions.map((t: WNodeAction, index: number) => {
        const eventId = t.params.event || 'notify_all_users'
        const eventLabel = makeWord(eventId)
        return (
          <Button color="info" key={index} onClick={() => onExecuteEvent(eventId)} disabled={loadingButton == eventId}>
            {loadingButton == (t.params.event || 'notify_all_users') ? 'Executing ' + eventLabel + '...' : eventLabel}
          </Button>
        )
      })}
    </>
  )
}

function getSubscribeRestaurantNodeActions(currentWorkflowNode: null | string, workflow: Workflow): WNodeAction[] {
  if (currentWorkflowNode) {
    const currentNode = workflow.nodes.find((n: WNode) => n.id == currentWorkflowNode)
    // const currentNode = workflow.nodes.find(
    //   (n: WNode) => null != n.actions.find((a: WNodeAction) => a.name == 'SubscribeRestaurant')
    // )
    if (currentNode) {
      const subscribeRestaurantAction = currentNode.actions.filter((a: WNodeAction) => a.name == 'SubscribeRestaurant')
      return subscribeRestaurantAction || null
    }
  }
  return []
}

interface ServerError {
  success: false
  reason: string
  status: number
  statusText: string
  error: any
}
function processJsonStatus<T = any>(res: Response): Promise<T> {
  return res.ok
    ? res.json()
    : res
        .json()
        .then((err) => Promise.reject(err))
        .catch((err) =>
          Promise.reject({
            success: false,
            reason: 'server-error',
            status: res.status,
            statusText: res.statusText,
            error: err,
          })
        )
}

const authHeaders = (token: string): any => {
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  }
}
